import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import { EditDescriptor } from "@progress/kendo-react-data-tools";
import {
  Grid,
  GridCellProps,
  GridColumn,
  GridItemChangeEvent,
  GridToolbar,
} from "@progress/kendo-react-grid";
import { Error } from "@progress/kendo-react-labels";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { useState } from "react";
import useAllocations from "../../hooks/useAllocations";
import { LoadingIndicator } from "../LoadingIndicator";
import { Commands, GridCommandCell } from "../grid/GridCommandCell";
import StringListGridCell from "../utilities/StringListGridCell";
import Allocation from "./Allocation";
import { AllocationReassignColumn } from "./AllocationReassignColumn";
import { default as buildAllocationColumnProps } from "./buildAllocationColumnProps";

const AllocationsGrid = () => {
  const {
    initialEditableAllocations,
    editableAllocations,
    setEditableAllocations,
    upsertAllocation,
    deleteAllocation,
    readOnlyAllocations,
    businessTypes,
    commodityTypes,
    companyId,
    isClient,
    companies,
    refreshAllocations,
    isLoading,
    isError,
    error,
  } = useAllocations();

  const [edit, setEdit] = useState<EditDescriptor>({});

  const showBusinessTypes = businessTypes.length > 1;

  const [errorMessage, setErrorMessage] = useState("");

  // Fires when edit button is clicked. Just make the row editable.
  const handleEdit = (dataItem: Allocation) => {
    const newGridData = editableAllocations.map((row: Allocation) => {
      let newRow = row;
      if (row.allocationId === dataItem.allocationId) {
        newRow = { ...row, inEdit: true };
      }

      return newRow;
    });
    setEdit({ [dataItem.allocationId]: true });
    setEditableAllocations(newGridData);
  };

  const handleCreate = () => {
    const newAllocation: Allocation = {
      businessTypeIds: [],
      commodityTypes: [],
      businessTypes,
      commodityTypeObjects: commodityTypes,
      companyId,
      name: "New Allocation",
      inEdit: true,
      allocationId: -1,
      1: 0,
      2: 0,
      3: 0,
      4: 0,
      5: 0,
      6: 0,
      7: 0,
      8: 0,
      9: 0,
      10: 0,
      11: 0,
      12: 0,
    };
    setEdit({ [newAllocation.allocationId]: true });
    const newGridData = [newAllocation].concat(editableAllocations);
    setEditableAllocations(newGridData);
  };

  const handleItemChange = ({ dataItem, field, value }: GridItemChangeEvent) => {
    dataItem = dataItem as Allocation;
    const newGridData = editableAllocations.map((allocation) => {
      if (allocation.allocationId === dataItem.allocationId) {
        const editedAllocation = { ...allocation, [field as string]: value };
        return editedAllocation;
      } else {
        return allocation;
      }
    });

    setEditableAllocations(newGridData);
  };

  const handleClickNew = () => {
    handleCreate();
  };

  const handleUpdate = (dataItem: Allocation) => {
    setEdit({});
    upsertAllocation(dataItem);
  };

  // Fires when canceling creation of new item
  const handleDiscard = (_dataItem: Allocation) => {
    const newGridData = editableAllocations.filter((allocation) => allocation.allocationId === -1);
    setErrorMessage("");
    setEditableAllocations(newGridData);
    setEdit({});
  };

  // Fires when canceling edit of existing allocation
  const handleCancel = (dataItem: Allocation) => {
    let newGridData = editableAllocations.map((allocation) => {
      if (allocation.allocationId === dataItem.allocationId) {
        allocation = initialEditableAllocations.find(
          (originalAllocation: Allocation) =>
            originalAllocation.allocationId === allocation.allocationId,
        );
      }
      return { ...allocation, inEdit: false };
    });
    newGridData = newGridData.filter((allocation) => !!allocation.allocationId);
    setEditableAllocations(newGridData);
    setEdit({});
  };

  // Click the trashcan to delete an existing allocation
  const handleRemove = (allocationId: number) => {
    deleteAllocation(allocationId);
  };

  const handleAdd = (dataItem: Allocation) => {
    if (dataItem.businessTypes.length === 0) {
      setErrorMessage("Please select one or more business areas to assign to the new allocation");
    } else {
      setErrorMessage("");
      handleUpdate(dataItem);
    }
  };

  const commands: Commands = {
    onAdd: handleAdd,
    onUpdate: handleUpdate,
    onDiscard: handleDiscard,
    onCancel: handleCancel,
    onEdit: handleEdit,
    onRemove: handleRemove,
  };

  const GridCommandCellRender = (props: GridCellProps) => {
    const { dataItem } = props;
    const description = `allocation ${dataItem.name} with Id ${dataItem.allocationId}`;

    return (
      <GridCommandCell {...props} {...commands} idField="allocationId" description={description} />
    );
  };

  const CommodityTypesGridCellRender = (props: GridCellProps) => {
    return <StringListGridCell allValues={commodityTypes} {...props} />;
  };

  const BusinessTypesGridCellRender = (props: GridCellProps) => {
    return <StringListGridCell allValues={businessTypes} {...props} />;
  };

  const ReassignColumnRender = (props: GridCellProps) => {
    return (
      <AllocationReassignColumn
        {...props}
        companies={companies}
        onReassigned={() => {
          // Refresh allocations after reassignment
          refreshAllocations();
        }}
      />
    );
  };

  const columnProps = buildAllocationColumnProps(
    showBusinessTypes,
    CommodityTypesGridCellRender,
    BusinessTypesGridCellRender,
  );

  const columns = columnProps.map((props, index) => {
    return <GridColumn key={index} {...props} />;
  });

  return (
    <Tooltip anchorElement="target" parentTitle={true}>
      <LoadingIndicator loading={isLoading} />
      {isError && <Error>{error}</Error>}
      <Grid
        data={editableAllocations}
        editable={{ enabled: true, mode: "inline" }}
        edit={edit}
        onItemChange={handleItemChange}
        dataItemKey="allocationId"
      >
        <GridToolbar>
          <Typography.h6>Editable Allocations</Typography.h6>
          <Button
            size="small"
            icon="plus"
            title="Create New Allocation"
            themeColor="primary"
            onClick={handleClickNew}
            disabled={Object.keys(edit).length !== 0}
          ></Button>
          {!!errorMessage && <Error>{errorMessage}</Error>}
        </GridToolbar>
        <GridColumn cells={{ data: GridCommandCellRender }} width={80} />
        {!isClient && <GridColumn cells={{ data: ReassignColumnRender }} width={40} />}
        {columns}
      </Grid>
      <div style={{ marginTop: 20 }}>
        <Typography.h6>Other Allocations</Typography.h6>
        <Grid data={readOnlyAllocations}>{columns}</Grid>
      </div>
    </Tooltip>
  );
};

export default AllocationsGrid;
